import { useEffect } from 'react'

/**
 * Promotes the source HTML's chrome (header + footer) so it's truly
 * common across every route, and undoes the two layout bugs that the
 * source HTML's structure introduces in a SPA context.
 *
 * Source layout (problematic in a SPA):
 *
 *   <body>
 *     <div id="root">
 *       <div data-pb-page="main">  <!-- React HtmlPage container -->
 *         <header id="nav"> ...sticky...
 *         <main class="page" id="page-home">    [contains its own footer]
 *         <main class="page" id="page-weguard"> [contains its own footer]
 *       </div>
 *     </div>
 *     <!-- pentabay-pages.js / pentabay-subpages.js append more mains
 *          DIRECTLY here, as siblings of #root, each carrying their
 *          own duplicate footer: -->
 *     <main class="page" id="page-services">  ...
 *     <main class="page" id="page-insights">  ...
 *     ...
 *   </body>
 *
 * Two consequences:
 *
 *   1. ~16 duplicate footers (one per page main).
 *   2. The sticky <header id="nav">'s containing block is the React
 *      container. For sub-page routes the container collapses to
 *      header height (home + weguard are display:none inside it, the
 *      active sub-page main is a body sibling OUTSIDE it), so the
 *      header sticks for ~80px and then scrolls away. The footer
 *      ends up between the header and the active sub-page content,
 *      which is what shows up as "header and footer collapsed".
 *
 * Fix, applied on mount and on every `pb-pages-mounted` event:
 *
 *   a. Move every <main class="page"> that isn't already inside the
 *      React container into it, so they're siblings of page-home and
 *      page-weguard. The header now sticks for the full active-page
 *      height (active main lives inside its containing block).
 *   b. Promote the first <footer> (with the <div class="hairline shell">
 *      that precedes it) into a [data-pb-common-footer] wrapper, and
 *      keep that wrapper as the LAST child of the container so it
 *      always renders below the active main.
 *   c. Delete any other footers (and their hairlines) still inside
 *      <main class="page"> elements.
 *
 * When HtmlPage unmounts (e.g. a 404 route), React tears the container
 * down and everything we re-parented or wrapped inside it goes with
 * it - no explicit DOM cleanup needed beyond detaching the listener.
 */
export default function FooterPromoter() {
  useEffect(() => {
    function precedingHairline(el) {
      const prev = el.previousElementSibling
      if (!prev) return null
      if (prev.classList.contains('hairline') && prev.classList.contains('shell')) return prev
      return null
    }

    function organize() {
      const container = document.querySelector('[data-pb-page="main"]')
      if (!container) return

      // (a) Bring every page main back into the React container so the
      //     header's sticky containing block always spans the active
      //     page's full height.
      document.querySelectorAll('main.page').forEach((main) => {
        if (!container.contains(main)) container.appendChild(main)
      })

      // (b) Promote first footer + its hairline into a shared wrapper.
      let common = container.querySelector(':scope > [data-pb-common-footer]')
      // Recover any stray wrapper that an older build left at body level.
      if (!common) {
        const stray = document.querySelector('[data-pb-common-footer]')
        if (stray) {
          container.appendChild(stray)
          common = stray
        }
      }
      if (!common) {
        const firstFooter = container.querySelector('main.page footer')
        if (firstFooter) {
          const hairline = precedingHairline(firstFooter)
          common = document.createElement('div')
          common.setAttribute('data-pb-common-footer', '')
          if (hairline) common.appendChild(hairline)
          common.appendChild(firstFooter)
          container.appendChild(common)
        }
      }

      // (c) Strip every duplicate footer (and its hairline) still inside
      //     a page main. The promoted one was lifted out in step (b) and
      //     lives in the shared wrapper, so it isn't matched here.
      container.querySelectorAll('main.page footer').forEach((f) => {
        const hairline = precedingHairline(f)
        if (hairline) hairline.remove()
        f.remove()
      })

      // (d) Keep the shared wrapper as the LAST child of the container
      //     so newly-arrived page mains (appended at the end by the
      //     source page-mount scripts) never end up below it.
      if (common && common.parentNode === container && common.nextSibling) {
        container.appendChild(common)
      }
    }

    organize()
    window.addEventListener('pb-pages-mounted', organize)
    return () => {
      window.removeEventListener('pb-pages-mounted', organize)
    }
  }, [])
  return null
}
